import { motion, useInView } from "framer-motion";
import { useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MessageCircle, Phone, Sparkles } from "lucide-react";
import { useIOSPortrait } from "@/hooks/useIOSPortrait";

// Generate random particles
function generateParticles(count: number) {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    size: Math.random() * 4 + 2, // 2px to 6px
    x: Math.random() * 100,
    y: Math.random() * 100,
    opacity: Math.random() * 0.4 + 0.3, // 0.3 to 0.7
    duration: Math.random() * 15 + 10, // 10s to 25s
    delay: Math.random() * 5,
    blur: Math.random() > 0.7 ? Math.random() * 2 : 0, // Some particles have blur
  }));
}

function Particle({ particle }: { particle: ReturnType<typeof generateParticles>[0] }) {
  const randomPath = useMemo(() => ({
    x: [0, Math.random() * 60 - 30, Math.random() * 40 - 20, Math.random() * 50 - 25, 0],
    y: [0, Math.random() * 50 - 25, Math.random() * 60 - 30, Math.random() * 40 - 20, 0],
    opacity: [particle.opacity, particle.opacity * 0.5, particle.opacity, particle.opacity * 0.7, particle.opacity],
    scale: [1, 1.2, 0.9, 1.1, 1],
  }), [particle.opacity]);

  return (
    <motion.div
      className="absolute rounded-full bg-foreground"
      style={{
        width: particle.size,
        height: particle.size,
        left: `${particle.x}%`,
        top: `${particle.y}%`,
        filter: particle.blur > 0 ? `blur(${particle.blur}px)` : undefined,
      }}
      animate={randomPath}
      transition={{
        duration: particle.duration,
        repeat: Infinity,
        ease: "easeInOut",
        delay: particle.delay,
      }}
    />
  );
}

// Static particle for iOS portrait - no animations
function StaticParticle({ particle }: { particle: ReturnType<typeof generateParticles>[0] }) {
  return (
    <div
      className="absolute rounded-full bg-foreground"
      style={{
        width: particle.size,
        height: particle.size,
        left: `${particle.x}%`,
        top: `${particle.y}%`,
        opacity: particle.opacity,
        filter: particle.blur > 0 ? `blur(${particle.blur}px)` : undefined,
      }}
    />
  );
}

export function FinalCTA() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const particles = useMemo(() => generateParticles(40), []);
  const isIOSPortrait = useIOSPortrait();

  // iOS Portrait: Render completely static version to prevent Framer Motion transforms
  if (isIOSPortrait) {
    return (
      <section ref={ref} className="section-padding bg-background relative overflow-hidden">
        {/* Static Particles Background */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          {particles.map((particle) => (
            <StaticParticle key={particle.id} particle={particle} />
          ))}
        </div>

        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30 z-[1]">
          <div className="absolute top-20 left-10 w-64 h-64 bg-blush rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-primary/20 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            {/* Decorative */}
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-rose text-primary-foreground mb-8">
              <Sparkles className="h-10 w-10" />
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-foreground mb-6">
              Ready to Start Your
              <br />
              <span className="text-gradient">Skincare Journey?</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto mb-10">
              Let us help you find the perfect products for your unique skin.{" "}
              <Link to="/products" className="text-primary hover:underline">
                Explore our curated skincare collection
              </Link>{" "}
              or read our{" "}
              <Link to="/blog" className="text-primary hover:underline">
                skincare tips and blog
              </Link>. 
              Chat with us on WhatsApp or give us a call—we're here to guide you every step of the way.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button variant="whatsapp" size="xl" asChild>
                <a
                  href="https://api.whatsapp.com/send?phone=2347065732481"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3"
                >
                  <MessageCircle className="h-5 w-5" />
                  Chat on WhatsApp
                </a>
              </Button>
              <Button variant="outline" size="xl" asChild>
                <a href="tel:07065732481" className="flex items-center gap-3">
                  <Phone className="h-5 w-5" />
                  Call Us Now
                </a>
              </Button>
            </div>

            {/* Trust Badge */}
            <p className="mt-8 text-sm text-muted-foreground">
              Fast responses • Expert advice • 100% authentic products
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} className="section-padding bg-background relative overflow-hidden">
      {/* Animated Particles Background */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        {particles.map((particle) => (
          <Particle key={particle.id} particle={particle} />
        ))}
      </div>

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30 z-[1]">
        <div className="absolute top-20 left-10 w-64 h-64 bg-blush rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-primary/20 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center"
        >
          {/* Decorative */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-rose text-primary-foreground mb-8 animate-float"
          >
            <Sparkles className="h-10 w-10" />
          </motion.div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-foreground mb-6">
            Ready to Start Your
            <br />
            <span className="text-gradient">Skincare Journey?</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto mb-10">
            Let us help you find the perfect products for your unique skin.{" "}
            <Link to="/products" className="text-primary hover:underline">
              Explore our curated skincare collection
            </Link>{" "}
            or read our{" "}
            <Link to="/blog" className="text-primary hover:underline">
              skincare tips and blog
            </Link>. 
            Chat with us on WhatsApp or give us a call—we're here to guide you every step of the way.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              variant="whatsapp"
              size="xl"
              asChild
            >
              <a
                href="https://api.whatsapp.com/send?phone=2347065732481"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3"
              >
                <MessageCircle className="h-5 w-5" />
                Chat on WhatsApp
              </a>
            </Button>
            <Button
              variant="outline"
              size="xl"
              asChild
            >
              <a
                href="tel:07065732481"
                className="flex items-center gap-3"
              >
                <Phone className="h-5 w-5" />
                Call Us Now
              </a>
            </Button>
          </div>

          {/* Trust Badge */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="mt-8 text-sm text-muted-foreground"
          >
            Fast responses • Expert advice • 100% authentic products
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}
